/**
 * Page Initialization for Tracking
 *
 * PURPOSE:
 * This script runs on every page load and:
 * 1. Reads page data embedded by Twig (window.__GO4JOBS_PAGE__)
 * 2. Pushes appropriate tracking events
 * 3. Handles static caching gracefully
 *
 * WHY THIS APPROACH?
 * - Static caching means we can't rely on server-side tracking
 * - We embed data in HTML, then JavaScript reads and tracks it
 * - This ensures tracking works even on cached pages
 */

import {
    trackViewItem,
    trackAddToCart,
    trackPurchase,
    trackSpontaneousApplication,
    trackBlogView,
    type GA4JobItem,
    type UserData
} from '~/js/utils/job-data-layer';

import { pushEvent } from '~/js/utils/datalayer';

interface PageData {
    type: 'job_detail' | 'application_page' | 'spontaneous_application' | 'blog_article';
    transactionId?: string;
    job?: GA4JobItem;
    formSubmitted?: boolean;
    blogTitle?: string;
    blogCategory?: string;
    userData?: UserData;
}

declare global {
    interface Window {
        __GO4JOBS_PAGE__?: PageData;
    }
}

async function sha256(str: string): Promise<string> {
    const buffer = new TextEncoder().encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
}

async function hashPii(email?: string | null, phone?: string | null): Promise<Record<string, string> | undefined> {
    const userData: Record<string, string> = {};
    if (email) {
        userData.sha256_email_address = await sha256(email.toLowerCase().trim());
    }
    if (phone) {
        let normalized = phone.replace(/[^\d+]/g, '');
        if (!normalized.startsWith('+')) {
            normalized = '+32' + normalized.replace(/^0/, '');
        }
        userData.sha256_phone_number = await sha256(normalized);
    }
    return Object.keys(userData).length > 0 ? userData : undefined;
}

/**
 * Initialize page-level tracking
 * Called on DOMContentLoaded
 */
export function initPageTracking(): void {
    const pendingPurchase = sessionStorage.getItem('__go4jobs_pending_purchase');
    
    if (pendingPurchase) {
        sessionStorage.removeItem('__go4jobs_pending_purchase');
        try {
            const { type, job, transactionId, email, phone } = JSON.parse(pendingPurchase);
            if (type === 'application' && job) {
                hashPii(email, phone).then(userData => {
                    trackPurchase(transactionId, job, userData);
                    if (userData) {
                        pushEvent('user_info_set', { user_data: userData });
                    }
                });
            } else if (type === 'spontaneous') {
                hashPii(email, phone).then(userData => {
                    trackSpontaneousApplication(transactionId, userData);
                    if (userData) {
                        pushEvent('user_info_set', { user_data: userData });
                    }
                });
            }
        } catch (e) {
            // Malformed data, ignore
        }
    }

    const pageData = window.__GO4JOBS_PAGE__;

    if (!pageData) {
        // No page data embedded - nothing to track
        return;
    }

    // Handle different page types
    switch (pageData.type) {
        case 'job_detail':
            handleJobDetailPage(pageData);
            break;

        case 'application_page':
            handleApplicationPage(pageData);
            break;

        case 'spontaneous_application':
            handleSpontaneousApplicationPage(pageData);
            break;

        case 'blog_article':
            handleBlogPage(pageData);
            break;
    }
}

/**
 * Handle job detail page tracking
 */
function handleJobDetailPage(pageData: PageData): void {
    if (!pageData.job) return;

    // Track view_item
    trackViewItem(pageData.job);
}

/**
 * Handle application page tracking
 */
function handleApplicationPage(pageData: PageData): void {
    if (!pageData.job) return;

    // Track add_to_cart (user initiated application process)
    trackAddToCart(pageData.job);

    // If form was just submitted successfully, track purchase
    if (pageData.formSubmitted && pageData.transactionId) {
        trackPurchase(
            pageData.transactionId,
            pageData.job,
            pageData.userData
        );
    }
}

/**
 * Handle spontaneous application page tracking
 */
function handleSpontaneousApplicationPage(pageData: PageData): void {
    if (pageData.formSubmitted && pageData.transactionId) {
        trackSpontaneousApplication(
            pageData.transactionId,
            pageData.userData
        );
    }
}

/**
 * Handle blog page tracking
 */
function handleBlogPage(pageData: PageData): void {
    if (pageData.blogTitle) {
        trackBlogView(pageData.blogTitle, pageData.blogCategory);
    }
}

/**
 * Initialize
 */
if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initPageTracking);
    } else {
        // DOM already loaded
        initPageTracking();
    }
}
